import { useQuery } from '@tanstack/react-query';
import { Text, Spinner, Badge, tokens } from '@fluentui/react-components';
import { useT } from '../../i18n';
import type { TelegramGroup } from '../../api/telegram-groups.api';
import { fetchGroupVigilados, type NivelCopia } from '../../api/supervision.api';

/** Panel de un grupo de supervisión: usuarios vigilados (con su tema) y supervisores. */
export function GroupWatchedPanel({ group }: { group: TelegramGroup }) {
  const t = useT();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['telegram-group-vigilados', group.chatId],
    queryFn: () => fetchGroupVigilados(group.chatId),
  });

  const levelLabel = (n: NivelCopia) =>
    n === 1 ? t.users.copyLevel1 : n === 2 ? t.users.copyLevel2 : n === 3 ? t.users.copyLevel3 : t.users.copyNone;

  if (isLoading) return <Spinner size="tiny" />;
  if (isError || !data) {
    return <Text role="alert" style={{ color: tokens.colorStatusDangerForeground1 }}>{t.telegramGroupsAdmin.watchedLoadError}</Text>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
        {t.telegramGroupsAdmin.watchedHint}
      </Text>

      <Text weight="semibold">{t.telegramGroupsAdmin.watchedToggle}</Text>
      {data.vigilados.length === 0 ? (
        <Text size={200}>{t.telegramGroupsAdmin.noWatched}</Text>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>{t.common.name}</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>{t.telegramGroupsAdmin.colLevel}</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>{t.telegramGroupsAdmin.colTopic}</th>
            </tr>
          </thead>
          <tbody>
            {data.vigilados.map(v => (
              <tr key={v.userId}>
                <td style={{ padding: '4px 6px' }}>{v.nombre || v.userId}</td>
                <td style={{ padding: '4px 6px' }}>{levelLabel(v.nivelCopia)}</td>
                <td style={{ padding: '4px 6px' }}>
                  {v.tema === null ? t.telegramGroupsAdmin.topicNone
                    : v.tema.estado === 'CERRADO' ? t.telegramGroupsAdmin.topicClosed
                    : t.telegramGroupsAdmin.topicOpen}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Text weight="semibold">{t.telegramGroupsAdmin.supervisorsTitle}</Text>
      {data.supervisores.length === 0 ? (
        <Text size={200}>{t.telegramGroupsAdmin.noSupervisors}</Text>
      ) : (
        <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {data.supervisores.map((s, i) => (
            <li key={`${s.nombre}-${i}`}>
              {s.nombre}{' '}
              <Badge appearance="tint" size="small" color={s.estado === 'verificado' ? 'success' : 'warning'}>
                {s.estado === 'verificado' ? t.telegramGroupsAdmin.verified : t.telegramGroupsAdmin.unverified}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
